import { i18n } from "../../translate/i18n";

import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet";

import api, { openApi } from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";

import "./loginCadastroModal.css";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

// resolve URL vinda do backend (relativa ou absoluta) com fallback
const resolveImageUrl = (value, fallback) => {
  if (!value) return fallback;
  if (value.startsWith("http")) return value;
  if (!backendUrl) return value;
  const base = backendUrl.replace(/\/+$/, "");
  const clean = value.replace(/^\/+/, "");
  return `${base}/${clean}`;
};

const Login = () => {
  const { handleLogin } = useContext(AuthContext);
  const { getPlanList } = usePlans();

  const [view, setView] = useState("login"); // login | register
  const [loginLoading, setLoginLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);

  const [user, setUser] = useState({
    email: "",
    password: "",
    remember: false,
  });

  const [register, setRegister] = useState({
    companyName: "",
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    planId: "",
  });

  const [plans, setPlans] = useState([]);

  const [branding, setBranding] = useState({
    loginBackground: null,
    loginWhatsapp: "https://wa.me/5500000000000",
  });

  const [userCreationEnabled, setUserCreationEnabled] = useState(true);
  const [recoverOpen, setRecoverOpen] = useState(false);
  const [recoverEmail, setRecoverEmail] = useState("");

  // ========= Tema e aparência da página =============
  useEffect(() => {
    try {
      localStorage.setItem("theme", "light");
    } catch (e) {}
    document.documentElement.classList.remove("dark");
    document.documentElement.setAttribute("data-theme", "light");
    document.body.classList.add("login-cadastro-modal");
    return () => document.body.classList.remove("login-cadastro-modal");
  }, []);

  // ========= Buscar settings globais ============
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const { data } = await api.get("/global-config/public-branding");
        setBranding((prev) => ({
          ...prev,
          loginBackground: data.loginBackground || null,
          loginWhatsapp: data.loginWhatsapp || "https://wa.me/5500000000000",
        }));
      } catch (err) {}
    };

    fetchBranding();
  }, []);

  // ========== Verificar se cadastro está habilitado ==========
  useEffect(() => {
    const fetchUserCreationStatus = async () => {
      try {
        const { data } = await api.get("/settings/userCreation");
        setUserCreationEnabled(data.userCreation === "enabled");
      } catch (err) {
        setUserCreationEnabled(false);
      }
    };

    fetchUserCreationStatus();
  }, []);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const planList = await getPlanList({ listPublic: "false" });
        setPlans(Array.isArray(planList) ? planList : []);
      } catch (err) {
        setPlans([]);
      }
    };
    loadPlans();
  }, [getPlanList]);

  useEffect(() => {
    if (!branding.loginBackground) {
      document.documentElement.style.setProperty("--login-bg-image", "none");
      return;
    }
    const bg = resolveImageUrl(branding.loginBackground);
    document.documentElement.style.setProperty("--login-bg-image", `url('${bg}')`);
  }, [branding.loginBackground]);

  const toggleForms = () => {
    const card = document.querySelector(".lcm-card");
    if (card) card.style.opacity = "0";
    window.setTimeout(() => {
      setView((v) => (v === "login" ? "register" : "login"));
      if (card) card.style.opacity = "1";
    }, 200);
  };

  const handleSubmitLogin = async (e) => {
    e.preventDefault();
    const lang = localStorage.getItem("i18nextLng") || "pt";
    i18n.changeLanguage(lang);
    try {
      setLoginLoading(true);
      await Promise.resolve(handleLogin(user));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSubmitRegister = async (e) => {
    e.preventDefault();
    if (register.password !== register.confirmPassword) {
      window.alert("Oops! As senhas informadas não coincidem.");
      return;
    }
    try {
      setRegisterLoading(true);
      await openApi.post("/auth/signup", {
        companyName: register.companyName,
        name: register.name,
        email: register.email,
        password: register.password,
        phone: register.phone,
        planId: register.planId,
      });
      window.alert("Cadastro realizado com sucesso! Agora você já pode fazer login.");
      setRegister({
        companyName: "",
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        phone: "",
        planId: "",
      });
      setView("login");
    } catch (err) {
      window.alert("Não foi possível realizar o cadastro. Verifique os dados e tente novamente.");
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleRecover = async () => {
    const email = recoverEmail.trim();
    if (!email) {
      window.alert("Informe um e-mail válido");
      return;
    }
    try {
      await api.post("/auth/forgot-password", { email });
      window.alert(`Link de redefinição enviado para ${email}`);
      setRecoverOpen(false);
      setRecoverEmail("");
    } catch (err) {
      window.alert("Não foi possível enviar o link. Tente novamente.");
    }
  };

  const formatWhatsapp = (raw) => {
    const value = String(raw || "").replace(/\D/g, "");
    const v = value.match(/(\d{0,2})(\d{0,5})(\d{0,4})/);
    return !v[2] ? v[1] : "(" + v[1] + ") " + v[2] + (v[3] ? "-" + v[3] : "");
  };

  return (
    <>
      <Helmet>
        <title>Login</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"
        />
      </Helmet>

      <div className="lcm-page">
        <div className="lcm-card">
          {/* ================= TELA DE LOGIN ================= */}
          <div id="login-container" className={view === "login" ? "" : "lcm-hidden"}>
            <div className="lcm-header">
              <h2>Bem-vindo de volta</h2>
              <p>Faça login para acessar sua conta</p>
            </div>

            <form id="form-login" onSubmit={handleSubmitLogin}>
              <div className="lcm-form-group">
                <label htmlFor="login-email">E-mail</label>
                <i className="fa-solid fa-envelope lcm-input-icon" />
                <input
                  type="email"
                  id="login-email"
                  placeholder="seu@email.com"
                  required
                  value={user.email}
                  onChange={(e) => setUser((p) => ({ ...p, email: e.target.value }))}
                />
              </div>

              <div className="lcm-form-group">
                <label htmlFor="login-senha">Senha</label>
                <i className="fa-solid fa-lock lcm-input-icon" />
                <input
                  type="password"
                  id="login-senha"
                  placeholder="••••••••"
                  required
                  value={user.password}
                  onChange={(e) => setUser((p) => ({ ...p, password: e.target.value }))}
                />
              </div>

              <div className="lcm-options">
                <label>
                  <input
                    type="checkbox"
                    id="lembrar-mim"
                    checked={user.remember}
                    onChange={(e) => setUser((p) => ({ ...p, remember: e.target.checked }))}
                  />
                  Lembrar de mim
                </label>
                <button type="button" className="linklike" onClick={() => setRecoverOpen(true)}>
                  Esqueceu a senha?
                </button>
              </div>

              <button
                type="submit"
                className={`lcm-btn lcm-btn-primary ${loginLoading ? "loading" : ""}`}
                disabled={loginLoading}
              >
                <span className="lcm-btn-text-content">Entrar</span>
                <div className="lcm-spinner" />
              </button>

              {userCreationEnabled && (
                <button type="button" className="lcm-btn lcm-btn-outline" onClick={toggleForms}>
                  Criar conta / Cadastro
                </button>
              )}
            </form>
          </div>

          {/* ================= TELA DE CADASTRO ================= */}
          <div
            id="register-container"
            className={view === "register" && userCreationEnabled ? "" : "lcm-hidden"}
          >
            <div className="lcm-header">
              <h2>Criar nova conta</h2>
              <p>Preencha os dados para se cadastrar</p>
            </div>

            <form id="form-registro" onSubmit={handleSubmitRegister}>
              <div className="lcm-form-group">
                <label htmlFor="reg-empresa">Nome da Empresa</label>
                <i className="fa-solid fa-building lcm-input-icon" />
                <input
                  type="text"
                  id="reg-empresa"
                  placeholder="Empresa S.A"
                  required
                  value={register.companyName}
                  onChange={(e) => setRegister((p) => ({ ...p, companyName: e.target.value }))}
                />
              </div>

              <div className="lcm-form-group">
                <label htmlFor="reg-usuario">Nome do Usuário</label>
                <i className="fa-solid fa-user lcm-input-icon" />
                <input
                  type="text"
                  id="reg-usuario"
                  placeholder="Seu nome completo"
                  required
                  value={register.name}
                  onChange={(e) => setRegister((p) => ({ ...p, name: e.target.value }))}
                />
              </div>

              <div className="lcm-form-group">
                <label htmlFor="reg-email">E-mail Corporativo</label>
                <i className="fa-solid fa-envelope lcm-input-icon" />
                <input
                  type="email"
                  id="reg-email"
                  placeholder="seu@email.com"
                  required
                  value={register.email}
                  onChange={(e) => setRegister((p) => ({ ...p, email: e.target.value }))}
                />
              </div>

              <div className="lcm-grid-2">
                <div className="lcm-form-group">
                  <label htmlFor="reg-senha">Senha</label>
                  <i className="fa-solid fa-lock lcm-input-icon" />
                  <input
                    type="password"
                    id="reg-senha"
                    placeholder="Mínimo 6 carac."
                    minLength={6}
                    required
                    value={register.password}
                    onChange={(e) => setRegister((p) => ({ ...p, password: e.target.value }))}
                  />
                </div>

                <div className="lcm-form-group">
                  <label htmlFor="reg-conf-senha">Confirmar Senha</label>
                  <i className="fa-solid fa-lock lcm-input-icon" />
                  <input
                    type="password"
                    id="reg-conf-senha"
                    placeholder="Repita a senha"
                    required
                    value={register.confirmPassword}
                    onChange={(e) =>
                      setRegister((p) => ({ ...p, confirmPassword: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="lcm-grid-2">
                <div className="lcm-form-group">
                  <label htmlFor="reg-whatsapp">WhatsApp</label>
                  <i className="fa-brands fa-whatsapp lcm-input-icon" />
                  <input
                    type="text"
                    id="reg-whatsapp"
                    placeholder="(11) 90000-0000"
                    maxLength={15}
                    required
                    value={register.phone}
                    onChange={(e) =>
                      setRegister((p) => ({ ...p, phone: formatWhatsapp(e.target.value) }))
                    }
                  />
                </div>

                <div className="lcm-form-group">
                  <label htmlFor="reg-plano">Selecione o Plano</label>
                  <i className="fa-solid fa-box lcm-input-icon" />
                  <select
                    id="reg-plano"
                    required
                    value={register.planId}
                    onChange={(e) => setRegister((p) => ({ ...p, planId: e.target.value }))}
                  >
                    <option value="" disabled>
                      Escolha...
                    </option>
                    {(plans.length ? plans : []).map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name}
                      </option>
                    ))}
                  </select>
                  <i className="fa-solid fa-chevron-down lcm-select-icon" />
                </div>
              </div>

              <button
                type="submit"
                className={`lcm-btn lcm-btn-primary ${registerLoading ? "loading" : ""}`}
                disabled={registerLoading}
              >
                <span className="lcm-btn-text-content">Cadastrar</span>
                <div className="lcm-spinner" />
              </button>

              <button type="button" className="lcm-btn-text" onClick={toggleForms}>
                <i className="fa-solid fa-arrow-left" /> Voltar para Login
              </button>
            </form>
          </div>
        </div>
      </div>

      {recoverOpen && (
        <div className="lcm-modal-backdrop" role="dialog" aria-modal="true">
          <div className="lcm-modal">
            <h3>Recuperar senha</h3>
            <p>Informe seu e-mail para receber o link de redefinição.</p>
            <input
              type="email"
              id="rec-email"
              placeholder="seu@email.com"
              required
              value={recoverEmail}
              onChange={(e) => setRecoverEmail(e.target.value)}
            />
            <button type="button" className="lcm-modal-primary" onClick={handleRecover}>
              Enviar link
            </button>
            <button type="button" className="lcm-modal-back" onClick={() => setRecoverOpen(false)}>
              Voltar para login
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className="lcm-whatsapp-fab"
        aria-label="Contato via WhatsApp"
        onClick={() => window.open(branding.loginWhatsapp, "_blank", "noopener,noreferrer")}
      >
        <i className="fa-brands fa-whatsapp" />
      </button>
    </>
  );
};

export default Login;
